import React, { useRef, useState, useEffect } from "react";
import "./VideoRecorder.css";

const VideoRecorder = ({ onRecordingComplete, onCancel }) => {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  useEffect(() => {
    initializeCamera();

    return () => {
      stopCamera();

      if (recordedVideo) {
        URL.revokeObjectURL(recordedVideo);
      }
    };
  }, []);

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      setIsCameraActive(true);
    } catch (error) {
      console.error("Error accessing camera:", error);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
  };

  const startRecording = () => {
    if (isCameraActive && streamRef.current) {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);

      const chunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks, { type: "video/mp4" });
        const videoURL = URL.createObjectURL(blob);
        setRecordedVideo(videoURL);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      const timerInterval = setInterval(updateTimer, 1000);
      mediaRecorderRef.current.timerInterval = timerInterval;
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(mediaRecorderRef.current.timerInterval);
      setIsRecording(false);
    }
  };

  const handleSave = () => {
    if (recordedVideo) {
      fetch(recordedVideo)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "recorded_video.mp4", {
            type: "video/mp4",
          });

          stopCamera();

          URL.revokeObjectURL(recordedVideo);

          onRecordingComplete(file);
        });
    }
  };

  const handleCancel = () => {
    if (recordedVideo) {
      URL.revokeObjectURL(recordedVideo);
    }

    stopCamera();

    onCancel();
  };

  const updateTimer = () => {
    setRecordingTime((prevTime) => prevTime + 1);
  };

  return (
    <div className="video-recorder">
      {!recordedVideo && <video ref={videoRef} autoPlay muted />}
      {recordedVideo && <video src={recordedVideo} controls />}
      {isRecording && (
        <div className="recording-timer">
          Recording: {Math.floor(recordingTime / 60)}:
          {(recordingTime % 60).toString().padStart(2, "0")}
        </div>
      )}
      <div className="video-recorder-button-container">
        {!isRecording && !recordedVideo && isCameraActive && (
          <button onClick={startRecording}>Start Recording</button>
        )}
        {isRecording && <button onClick={stopRecording}>Stop Recording</button>}
        {recordedVideo && (
          <>
            <button onClick={handleSave}>Save</button>
            <button
              onClick={() => {
                if (recordedVideo) {
                  URL.revokeObjectURL(recordedVideo);
                }
                setRecordedVideo(null);
                initializeCamera();
              }}
            >
              Re-record
            </button>
          </>
        )}
        <button onClick={handleCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default VideoRecorder;
